import { useState, useEffect } from "react";
import { handleError } from "utils";
import constants from "../../constants";
import {
  attachProfilePicFromChannelMembers,
  getDefaultProfileImage,
  getOtherChannelMembers,
  getOtherSendbirdMembers,
  isTeamMessengerChannel,
  parseChannelMembers,
  resolveMessengerGroupMemberCount,
} from "./channelData.helpers";
const MESSAGES_LIMIT = 4;

const useContactInfoState = (dependencies) => {
  const {
    channel,
    currentUser,
    setCurrentChannel,
    handleProfileBtnClose,
    sdk,
    t,
  } = dependencies;
  const [isDeleteConversationPopupOpen, setIsDeleteConversationPopupOpen] =
    useState(false);
  const [isMediaDocActive, setMediaDocActive] = useState(false);
  const [isUpdateGroupPopupOpen, setIsUpdateGroupPopupOpen] = useState(false);
  const [mediaMessages, setMediaMessages] = useState([]);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);

  const [isPopOpen, setIsPopOpen] = useState(false);
  const [isConversationStateLoading, setIsConversationStateLoading] =
    useState(false);

  const isConversationClosed =
    channel?.customType === constants.chat.patient.close;

  const handleSeeAllBtn = () => {
    setMediaDocActive(true);
  };
  const handleMediaDocBackBtn = () => {
    setMediaDocActive(false);
  };

  const deleteChat = async () => {
    setActionInProgress(true);
    try {
      await channel.delete();
      setCurrentChannel(null);
      handleProfileBtnClose();
    } catch (error) {
      handleError(error);
    }
    setActionInProgress(false);
    setIsDeleteConversationPopupOpen(false);
  };

  const toggleChat = async (channelUrl, customType) => {
    if (!channelUrl) {
      return;
    }
    setIsConversationStateLoading(true);

    try {
      const getgrpChannel = await sdk.groupChannel.getChannel(channelUrl);
      const updateChannel = await getgrpChannel.updateChannel({
        customType: customType,
      });
      setIsPopOpen(false);
      return updateChannel;
    } catch (error) {
      handleError(error);
    } finally {
      setIsConversationStateLoading(false);
    }
  };

  const toggleChatOpenState = async () => {
    try {
      if (isConversationClosed) {
        return await toggleChat(channel._url, constants.chat.patient.open);
      }
      return await toggleChat(channel._url, constants.chat.patient.close);
    } catch (error) {
      handleError(error);
    }
  };

  const removeMember = async (memberId, updateChannelList) => {
    if (!channel || !sdk) {
      return;
    }

    const isCreator = channel?.creator?.userId === currentUser?.id?.toString();
    if (!isCreator) {
      handleError(
        new Error(
          t
            ? t("messenger.removeMemberError")
            : "Only the group creator can remove members"
        )
      );
      return;
    }

    setActionInProgress(true);
    try {
      const groupChannel = await sdk.groupChannel.getChannel(channel.url);

      const memberToRemove = groupChannel.members.find(
        (member) => member.userId === memberId.toString()
      );

      if (!memberToRemove) {
        handleError(new Error("Member not found in channel"));
        setActionInProgress(false);
        return;
      }

      // Use kick instead of ban - allows re-adding
      await groupChannel.inviteWithUserIds([memberToRemove.userId], {
        onCompleted: async () => {
          // Remove immediately after invite to kick
          await groupChannel.leave(memberToRemove);
        },
      });

      const synced = await sdk.groupChannel.getChannel(channel.url);
      const rawData = synced?.data ?? groupChannel?.data ?? channel.data;
      let channelData = {};
      try {
        channelData = rawData ? JSON.parse(rawData) : {};
      } catch {
        channelData = {};
      }
      if (channelData.members) {
        channelData.members = channelData.members.filter(
          (mem) => mem.id.toString() !== memberId.toString()
        );

        await synced.updateChannel({
          data: JSON.stringify(channelData),
        });

        const latestChannel = await sdk.groupChannel.getChannel(channel.url);
        setCurrentChannel(latestChannel);

        if (updateChannelList && typeof updateChannelList === "function") {
          updateChannelList();
        }
      } else {
        const latestChannel = await sdk.groupChannel.getChannel(channel.url);
        setCurrentChannel(latestChannel);
        if (updateChannelList && typeof updateChannelList === "function") {
          updateChannelList();
        }
      }
    } catch (error) {
      handleError(error);
    }
    setActionInProgress(false);
  };

  useEffect(() => {
    if (channel?.url) {
      const mediaListQueryParam = {
        customTypesFilter: ["image", "document"],
        limit: MESSAGES_LIMIT,
        reverse: true,
      };
      const query = channel.createPreviousMessageListQuery(mediaListQueryParam);
      if (query) {
        (async () => {
          try {
            setIsMediaLoading(true);
            const msgs = await query?.load();
            setMediaMessages(msgs);
          } catch (error) {
            handleError(error);
          }
          setIsMediaLoading(false);
        })();
      }
    }
  }, [channel]);

  const getChannelData = () => {
    try {
      if (!channel) return {};
      const isTeam = isTeamMessengerChannel(channel);
      const {
        name: groupName,
        coverUrl,
        memberCount,
        members,
        data,
      } = channel || {};

      let channelName = groupName;
      let officeName = "";
      let profilePic = coverUrl || getDefaultProfileImage();
      let emailId = "";
      let customMembers = parseChannelMembers(data);
      let createdBy = channel?.creator?.nickname;
      let contactNumber = "";

      const resolvedMemberCount = resolveMessengerGroupMemberCount(
        memberCount,
        members,
        customMembers,
      );

      if (data && resolvedMemberCount === 2) {
        const otherCustomUser = getOtherChannelMembers(
          customMembers,
          currentUser?.id,
        )?.[0];
        const otherSendbirdMember = getOtherSendbirdMembers(
          members,
          currentUser?.id,
        )?.[0];
        emailId = otherCustomUser?.emailId;
        officeName = isTeam ? "" : otherCustomUser?.officeName;
        channelName =
          otherSendbirdMember?.nickname || otherCustomUser?.name || groupName;
        profilePic =
          otherSendbirdMember?.plainProfileUrl || getDefaultProfileImage();
        contactNumber = otherCustomUser?.contactNumber;
      } else if (data && resolvedMemberCount > 2) {
        customMembers = attachProfilePicFromChannelMembers(
          customMembers,
          channel?.members || [],
        );
        if (isTeam && Array.isArray(customMembers)) {
          customMembers = customMembers.map((m) => ({
            ...m,
            officeName: null,
          }));
        }
      }

      return {
        emailId,
        officeName,
        channelName,
        channelImage: profilePic,
        customMembers,
        createdBy,
        memberCount: resolvedMemberCount,
        contactNumber,
      };
    } catch {
      return {};
    }
  };

  return {
    state: {
      isDeleteConversationPopupOpen,
      isMediaDocActive,
      isUpdateGroupPopupOpen,
      mediaMessages,
      isMediaLoading,
      actionInProgress,
      isPopOpen,
      isConversationStateLoading,
      isConversationClosed,
    },
    updateMethods: {
      setIsDeleteConversationPopupOpen,
      setMediaDocActive,
      setIsUpdateGroupPopupOpen,
      setIsMediaLoading,
    },
    otherMethods: {
      getChannelData,
      handleSeeAllBtn,
      handleMediaDocBackBtn,
      deleteChat,
      setIsPopOpen,
      toggleChatOpenState,
      removeMember,
    },
  };
};

export default useContactInfoState;
